import { useState, useEffect } from "react";
import MultiFileUploader from "../../../common/MultiFileUploader";
import Drawer from "../../../drawer/Drawer";
import { mediaExtensions } from "../../../constant/constant";
import { toast } from "react-toastify";
import { fileAddEdit } from "../../../../services/mediaService";
import { useSearchParams } from "react-router-dom";
import Button from "../../../form/Button";
import Input from "../../../form/Input";
import { supportedLanguages } from "../../../../helpers";
import LanguageTabs from "../../../elements/LanguageSwitcher";

const ManageFile = ({
  open,
  close = () => { },
  getResponse = () => { },
  data = {},
}) => {
  const [selectedFile, setSelectedFile] = useState([]);
  const [progress, setProgress] = useState(0);
  const [searchParams] = useSearchParams();
  const folderData = searchParams.get("folderData");
  const [loading, setLoading] = useState(false);
  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("lang") || "en",
  );
  const [caption, setCaption] = useState({});
  const [altText, setAltText] = useState({});

  // 🔄 Reset states whenever the drawer opens or new data is passed
  useEffect(() => {
    if (!open) return; // only run when drawer opens

    const captions = {};
    const alts = {};

    supportedLanguages.forEach((lang) => {
      captions[lang.code] = data?.content?.[lang.code]?.caption || "";
      alts[lang.code] = data?.content?.[lang.code]?.altText || "";
    });

    setCaption(captions);
    setAltText(alts);
    setSelectedFile([]);
    setProgress(0);
  }, [open, data]);

  const handleLangChange = (lang) => setCurrentLang(lang);

  const handleFile = (newFileList) => {
    if (!newFileList?.length) return;
    const allowed = Object.values(mediaExtensions).flat();

    const validFiles = newFileList.filter((file) => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!allowed.includes(ext)) {
        toast.warning(`"${file.name}" is not an allowed file type.`);
        return false;
      }
      return true;
    });

    if (validFiles.length) setSelectedFile(validFiles);
  };

  const onSubmit = async () => {
    if (!selectedFile?.length) {
      toast.error("Please select at least one file.");
      return;
    }

    setLoading(true);

    const content = supportedLanguages.reduce((acc, lang) => {
      acc[lang.code] = {
        caption: caption[lang.code] || "",
        altText: altText[lang.code] || "",
      };
      return acc;
    }, {});

    const payload = {
      media: selectedFile,
      lang: localStorage.getItem("lang"),
      slug: `${(caption[currentLang] || "file")
        .replace(/\s+/g, "-")
        .toLowerCase()}-${Date.now()}`,
      captions: content,
      ...(data?._id && { id: data._id }),
      ...(folderData && folderData !== "root" && { key: folderData }),
    };

    try {
      const result = await fileAddEdit(payload, setProgress);
      if (result?.status === 200 || result?.status === 201) {
        toast.success(result.message || "File saved successfully");
        getResponse(result);
        close(); // ✅ properly close drawer
      } else {
        toast.error(result.message || "Failed to save file");
      }
    } catch (error) {
      toast.error(error.message || "Failed to save file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      actualSize="xl"
      size="xl2"
      title="Upload File"
      drawerBodyClass="p-0 max-h-[calc(100%-90px)]"
    >
      <div className="h-full overflow-y-auto p-3">
        {/* <LanguageTabs
          languages={supportedLanguages}
          defaultLang="en"
          value={currentLang}
          onChange={handleLangChange}
        /> */}

        <div className="mb-4">
          <Input
            label="Caption"
            labelClasses="rounded-2xl"
            inputPlaceholder="Add caption"
            value={caption[currentLang] || ""}
            onChange={(e) =>
              setCaption((prev) => ({
                ...prev,
                [currentLang]: e.target.value,
              }))
            }
          />
        </div>

        <div className="mb-4">
          <Input
            label="Alt Text"
            inputPlaceholder="Insert alt text"
            value={altText[currentLang] || ""}
            onChange={(e) =>
              setAltText((prev) => ({
                ...prev,
                [currentLang]: e.target.value,
              }))
            }
          />
        </div>

        <div className="text-xs font-medium text-text-disabled  ">
          Media
        </div>

        <MultiFileUploader
          handleFile={handleFile}
          progress={progress}
          selectedFile={selectedFile}
        />

        <div className="flex justify-end pt-8">
          <Button
            buttonType="button"
            buttonClasses="min-w-36"
            buttonLabel="Save"
            {...(loading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
            buttonFunction={onSubmit}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default ManageFile;
